import { useState, useEffect, useCallback } from 'react';
import { RotateCcw, Trash2, Trash, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  listTrash, restoreFromTrash, permanentlyDelete, clearTrash, purgeExpiredTrash,
  trashRemainingDays, TRASH_RETENTION_DAYS, type TrashItem,
} from '@/lib/trash';

interface TrashPanelProps {
  showTitle?: boolean;
}

export function TrashPanel({ showTitle = true }: TrashPanelProps) {
  const [items, setItems] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const purged = await purgeExpiredTrash();
    const list = await listTrash();
    setItems(list);
    setLoading(false);
    if (purged > 0) {
      // 静默清除，不打扰用户
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    purgeExpiredTrash()
      .then(() => listTrash())
      .then((list) => {
        if (cancelled) return;
        setItems(list);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleRestore = async (item: TrashItem) => {
    const restored = await restoreFromTrash(item.trashId);
    if (restored) {
      alert(`已恢复「${restored.summary}」到${restored.sourceLabel}`);
      refresh();
    } else {
      alert('恢复失败：未找到该记录');
    }
  };

  const handlePermanentDelete = async (item: TrashItem) => {
    if (!window.confirm(`确定永久删除「${item.summary}」吗？\n永久删除后不可恢复。`)) return;
    await permanentlyDelete(item.trashId);
    refresh();
  };

  const handleClearAll = async () => {
    if (!items.length) return;
    if (!window.confirm(`确定清空回收站（共 ${items.length} 条）吗？\n清空后所有记录将永久删除，不可恢复。`)) return;
    await clearTrash();
    refresh();
  };

  return (
    <div className="flex flex-col h-full">
      {showTitle && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold text-zinc-900">回收站</span>
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 font-mono">{items.length} 条</span>
          </div>
          <Button variant="outline" size="sm" className="h-8 text-[12px] border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-40 gap-1" disabled={!items.length} onClick={handleClearAll}>
            <Trash className="w-3.5 h-3.5" /> 清空回收站
          </Button>
        </div>
      )}

      <div className="px-4 py-2.5 bg-amber-50/60 border border-amber-100 rounded-lg text-[11.5px] text-amber-700 mb-3">
        已删除的数据会在这里保留 <strong>{TRASH_RETENTION_DAYS} 天</strong>，逾期自动清除。期间可随时恢复。
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pr-1">
        {loading ? (
          <div className="text-center py-10 text-[13px] text-zinc-400">加载中...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <Trash2 className="w-10 h-10 text-zinc-200 mx-auto mb-2" />
            <p className="text-[13px] text-zinc-400">回收站是空的</p>
          </div>
        ) : (
          items.map((item) => {
            const days = trashRemainingDays(item);
            return (
              <div key={item.trashId} className="flex items-center gap-3 border border-zinc-200 rounded-lg px-3.5 py-3 hover:border-zinc-300 bg-white">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12.5px] font-medium text-zinc-800 truncate">{item.summary}</span>
                    <span className="text-[10.5px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 flex-shrink-0">{item.sourceLabel}</span>
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    删除于 {new Date(item.deletedAt).toLocaleString('zh-CN')}
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1.5">
                  <span className={`text-[11px] px-1.5 py-0.5 rounded ${days <= 1 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-700'}`}>
                    {days > 0 ? `剩 ${days} 天` : '即将清除'}
                  </span>
                  <button
                    onClick={() => handleRestore(item)}
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-[11.5px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200"
                    title="恢复到原位置"
                  >
                    <RotateCcw className="w-3 h-3" /> 恢复
                  </button>
                  <button
                    onClick={() => handlePermanentDelete(item)}
                    className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-[11.5px] font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200"
                    title="永久删除（不可恢复）"
                  >
                    <Trash2 className="w-3 h-3" /> 删除
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {items.length > 0 && (
        <div className="mt-3 text-[11px] text-zinc-400 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> 恢复的数据会回到对应列表末尾；超过 {TRASH_RETENTION_DAYS} 天未恢复的记录将被自动清除
        </div>
      )}
    </div>
  );
}
